/* Host wrapper that opens the unified DepEditorModal against a Takt section on
 * the takt board. There is no draft here — the Takt board commits dependency
 * edits immediately. The host:
 *   - loads the flat, already-cascaded scheduled graph from the ONE endpoint
 *     (fetchDepEditorGraph with the accumulated edge edits as a transient
 *     draft, so the gantt strip re-cascades live as the user edits),
 *   - translates the modal's edit intents into edge_edits ops,
 *   - on Save opens the cascade workbench seeded with the staged edits: the
 *     user reviews the cross-section impact, can stage recovery edits
 *     (durations, lag/type on impacted rows), and applies everything under
 *     one batch_id via the workbench endpoints. */
import React from "react";
import DepEditorModal from "../../pull-plan/DepEditorModal/DepEditorModal";
import { type ExternalCandidate } from "../../pull-plan/depCore/DepChipTypeahead/DepChipTypeahead";
import {
  fetchDepEditorGraph,
  searchActivities,
  trackEvent,
  workbenchApply,
} from "../../../api/clientV2";
import {
  emptyDraftPayload,
  predTypeFromShort,
  type DepEditOp,
  type DepGraph,
  type DepGraphPredType,
  type DraftPayload,
  type TaktColorEntry,
  type WorkbenchBody,
} from "../../../models";
import CascadeWorkbenchModal from "../../shared/cascadeWorkbench/CascadeWorkbenchModal/CascadeWorkbenchModal";
import {
  captureBoardDates,
  stageDurationEdit,
  toExternalCandidates,
  toPendingDurationEdits,
  type BoardDates,
} from "./depEditHelpers";

interface Props {
  scheduleId: number;
  sectionName: string;
  sectionTitle: string;
  /** Takt-board context passed through for the cascade workbench. */
  colors: Record<string, TaktColorEntry>;
  activityWorkCodeMap: Map<number, string>;
  sectionMap: Map<number, string>;
  zoneMap: Map<number, string>;
  /** Fired after a successful save so the takt board can reload its grid. */
  onSaved: () => void;
  onClose: () => void;
}

/** A Takt-board endpoint id is always a real (committed) activity id — there
 *  are no draft new activities on this surface. */
function asActivityId(graphId: string): number {
  return Number(graphId);
}

const TaktSectionDepEditorHost: React.FC<Props> = ({
  scheduleId,
  sectionName,
  sectionTitle,
  colors,
  activityWorkCodeMap,
  sectionMap,
  zoneMap,
  onSaved,
  onClose,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [graph, setGraph] = React.useState<DepGraph>({ activities: {}, edges: {}, data_date: null });
  // Accumulated edge edits since the editor opened. Committed in one batch on
  // Save. Also re-sent as a transient draft on each change so the graph
  // re-cascades live (the Takt board has no persisted draft).
  const [edits, setEdits] = React.useState<DepEditOp[]>([]);
  // Staged duration changes (activity_id → workdays) — re-sent as a transient
  // draft so the live pass previews them, and committed with the edits on Save.
  const [durationEdits, setDurationEdits] = React.useState<Map<number, number>>(
    () => new Map(),
  );
  // The section's committed dates, frozen at open — what "Board dates" mode
  // shows. Captured from the first (no-edit) graph; "Forward pass" mode shows
  // the live re-cascade instead.
  const [boardDates, setBoardDates] = React.useState<BoardDates | undefined>(
    undefined,
  );
  const baselineCapturedRef = React.useRef(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  React.useEffect(() => {
    void trackEvent("dep_editor_opened", { section: sectionName });
  }, [sectionName]);

  // Re-fetch the cascaded graph whenever the edit set changes. The first load
  // (no edits) omits the draft to get the committed graph; subsequent loads
  // pass the edits as pending_dep_edits so the strip animates the cascade.
  const fetchSeqRef = React.useRef(0);
  React.useEffect(() => {
    let cancelled = false;
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    const hasEdits = edits.length > 0 || durationEdits.size > 0;
    const draft: DraftPayload | undefined = hasEdits
      ? {
          ...emptyDraftPayload(),
          pending_dep_edits: edits,
          pending_duration_edits: toPendingDurationEdits(durationEdits),
        }
      : undefined;
    fetchDepEditorGraph(scheduleId, sectionName, draft)
      .then((g) => {
        if (cancelled || seq !== fetchSeqRef.current) return;
        setGraph(g);
        // The first committed graph (no staged edits) is the board baseline.
        if (!hasEdits && !baselineCapturedRef.current) {
          baselineCapturedRef.current = true;
          setBoardDates(captureBoardDates(g));
        }
      })
      .catch((e: any) => {
        if (cancelled || seq !== fetchSeqRef.current) return;
        setError(e?.response?.data?.error ?? e?.message ?? "Failed to load section.");
      })
      .finally(() => {
        if (!cancelled && seq === fetchSeqRef.current) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scheduleId, sectionName, edits, durationEdits]);

  // Out-of-section search for the pred/succ "+ add" tie pickers — every activity
  // not already in this section's scope. Mirrors the pull plan's typeahead.
  const graphRef = React.useRef(graph);
  graphRef.current = graph;
  const fetchExternals = React.useCallback(
    async (q: string): Promise<ExternalCandidate[]> => {
      const results = await searchActivities(scheduleId, q, 25);
      const g = graphRef.current;
      const inScope = new Set(
        Object.values(g.activities).filter((a) => a.in_scope).map((a) => a.id),
      );
      return toExternalCandidates(results, inScope);
    },
    [scheduleId],
  );

  const handleEditDuration = React.useCallback(
    (graphId: string, days: number) => {
      const activityId = asActivityId(graphId);
      const committed = graphRef.current.activities[graphId]?.duration_days;
      setDurationEdits((prev) =>
        stageDurationEdit(prev, activityId, days, committed),
      );
    },
    [],
  );

  // --- edit-intent handlers: translate (succId, predId) graph ids into the
  // edge_edits ops the backend persists. A committed edge carries its DB
  // edge_id; an edge added earlier in this session is represented by its own
  // "add" op (no DB id yet), so a later retype/remove rewrites that op in place
  // rather than emitting a stale update/remove. -----------------------------

  const findEdge = React.useCallback(
    (succId: string, predId: string) =>
      (graph.edges[succId] || []).find((e) => e.pred_id === predId) ?? null,
    [graph],
  );

  const handleAddEdge = React.useCallback(
    (predId: string, succId: string) => {
      setEdits((prev) => [
        ...prev,
        {
          op: "add",
          pred_activity_id: asActivityId(predId),
          succ_activity_id: asActivityId(succId),
          pred_type: "PR_FS",
          lag_hours: 0,
        },
      ]);
    },
    [],
  );

  const handleRemoveEdge = React.useCallback(
    (succId: string, predId: string) => {
      const edge = findEdge(succId, predId);
      if (edge == null) return;
      if (edge.edge_id != null) {
        // Committed edge — drop any staged update on it, then remove it.
        setEdits((prev) => [
          ...prev.filter(
            (op) => !("edge_id" in op) || op.edge_id !== edge.edge_id,
          ),
          { op: "remove", edge_id: edge.edge_id! },
        ]);
        return;
      }
      // Draft-added edge — cancel the add that created it.
      const pred = asActivityId(predId);
      const succ = asActivityId(succId);
      setEdits((prev) =>
        prev.filter(
          (op) =>
            !(
              op.op === "add" &&
              op.pred_activity_id === pred &&
              op.succ_activity_id === succ
            ),
        ),
      );
    },
    [findEdge],
  );

  const handleRetypeEdge = React.useCallback(
    (succId: string, predId: string, type: DepGraphPredType, lagDays: number) => {
      const edge = findEdge(succId, predId);
      if (edge == null) return;
      const predType = predTypeFromShort(type);
      const lagHours = lagDays * 8;
      if (edge.edge_id != null) {
        const edgeId = edge.edge_id;
        setEdits((prev) => [
          ...prev.filter((op) => !("edge_id" in op) || op.edge_id !== edgeId),
          { op: "update", edge_id: edgeId, pred_type: predType, lag_hours: lagHours },
        ]);
        return;
      }
      // Draft-added edge — rewrite its add op in place.
      const pred = asActivityId(predId);
      const succ = asActivityId(succId);
      setEdits((prev) =>
        prev.map((op) =>
          op.op === "add" &&
          op.pred_activity_id === pred &&
          op.succ_activity_id === succ
            ? { ...op, pred_type: predType, lag_hours: lagHours }
            : op,
        ),
      );
    },
    [findEdge],
  );

  // --- save: open the workbench seeded with the staged edits ---------------
  // Frozen copies so the workbench gets stable inputs. Section/global drift (e.g.
  // a data-date advance) is reconciled by F9 Simulate, flagged by the F9 badge —
  // not here, so Save only ever cascades the explicit edits.
  const [workbenchOpen, setWorkbenchOpen] = React.useState(false);
  const [workbenchEdits, setWorkbenchEdits] = React.useState<DepEditOp[]>([]);
  const [workbenchDurations, setWorkbenchDurations] = React.useState<
    Map<number, number>
  >(() => new Map());

  const handleSave = React.useCallback(() => {
    setSaveError(null);
    if (edits.length === 0 && durationEdits.size === 0) {
      onClose();
      return;
    }
    setWorkbenchEdits([...edits]);
    setWorkbenchDurations(new Map(durationEdits));
    setWorkbenchOpen(true);
  }, [edits, durationEdits, onClose]);

  const handleWorkbenchApply = React.useCallback(
    (body: WorkbenchBody) => workbenchApply(scheduleId, body),
    [scheduleId],
  );

  const handleWorkbenchApplied = React.useCallback(() => {
    void trackEvent("dep_editor_saved", { section: sectionName });
    setWorkbenchOpen(false);
    onSaved();
  }, [sectionName, onSaved]);

  const workbenchCount = workbenchEdits.length + workbenchDurations.size;

  return (
    <>
      <DepEditorModal
        sectionTitle={sectionTitle}
        graph={graph}
        boardDates={boardDates}
        onAddEdge={handleAddEdge}
        onRemoveEdge={handleRemoveEdge}
        onRetypeEdge={handleRetypeEdge}
        onEditDuration={handleEditDuration}
        fetchExternals={fetchExternals}
        onClose={onClose}
        onSave={handleSave}
        saveDisabled={workbenchOpen}
        closeOnSave={false}
        loading={loading}
        error={error ?? saveError}
      />
      {workbenchOpen && (
        <CascadeWorkbenchModal
          scheduleId={scheduleId}
          initialLogicEdits={workbenchEdits}
          initialDurationEdits={workbenchDurations}
          headerLabel={`${sectionTitle} — ${workbenchCount} change${workbenchCount === 1 ? "" : "s"}`}
          colors={colors}
          activityWorkCodeMap={activityWorkCodeMap}
          sectionMap={sectionMap}
          zoneMap={zoneMap}
          onCancel={() => setWorkbenchOpen(false)}
          onApply={handleWorkbenchApply}
          onApplied={handleWorkbenchApplied}
        />
      )}
    </>
  );
};

export default TaktSectionDepEditorHost;
